import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import AccuracyGuidelineChatSidebar from '../components/AccuracyGuidelineChatSidebar';
import { GuidelineDraftEditor } from '../components/GuidelineDraftEditor';
import {
  createTuningSession as createTuningSessionApi,
  getBaselineErrors as getBaselineErrorsApi,
  getEvaluationRunErrors as getEvaluationRunErrorsApi,
  listEvaluationRuns as listEvaluationRunsApi,
} from '../services/accuracyBackendApi';
import { applyProposalToDraftContent } from '../services/guidelineDraftApply';
import { buildTuningDraftChatContext } from '../services/accuracyTuningDraftChat';
import {
  loadTuningDraftSeed,
  type DraftCompareTargetId,
} from '../services/accuracyTuningDraftSeed';
import { buildAccuracyReportDetailPath } from '../services/accuracyReportPaths';
import type { GuidelineDraftContent, SavedAccuracyReport } from '../types/accuracy';
import type { GuidelineChatProposal } from '../types/guidelineProposal';
import { useToast } from '../components/toast/ToastProvider';
import '../pages/AccuracyCountDrilldownPage.css';
import '../pages/AccuracyTuningPages.css';

const AccuracyTuningDraftPage: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<GuidelineDraftContent | null>(null);
  const [report, setReport] = useState<SavedAccuracyReport | null>(null);
  const [sessionName, setSessionName] = useState('');
  const [compareTargets, setCompareTargets] = useState<
    Awaited<ReturnType<typeof loadTuningDraftSeed>>['compareTargets']
  >([]);
  const [compareTargetId, setCompareTargetId] = useState<DraftCompareTargetId>('baseline');
  const [chatErrors, setChatErrors] = useState<Awaited<ReturnType<typeof getBaselineErrorsApi>>>(
    []
  );
  const [errorSourceLabel, setErrorSourceLabel] = useState('ベースライン評価');

  useEffect(() => {
    if (!reportId) return;
    (async () => {
      setLoading(true);
      try {
        const seed = await loadTuningDraftSeed(reportId);
        setReport(seed.report);
        setDraft(seed.initialDraft);
        setSessionName(`${seed.report.name} — 調整`);
        setCompareTargets(seed.compareTargets);
        setCompareTargetId(seed.compareTargets[0]?.id ?? 'baseline');

        let errors = await getBaselineErrorsApi(reportId);
        let source = 'ベースライン評価';
        if (seed.latestSession) {
          try {
            const runs = await listEvaluationRunsApi(seed.latestSession.id);
            const latestRun = runs.sort((a, b) => b.run_number - a.run_number)[0];
            if (latestRun) {
              const runErrors = await getEvaluationRunErrorsApi(latestRun.id);
              if (runErrors.length > 0) {
                errors = runErrors;
                source = `チューニング #${latestRun.run_number}`;
              }
            }
          } catch {
            // keep baseline errors
          }
        }
        setChatErrors(errors);
        setErrorSourceLabel(source);
      } catch {
        setDraft(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [reportId]);

  const chatContext = useMemo(() => {
    if (!report || !draft) return null;
    return buildTuningDraftChatContext({
      report,
      errors: chatErrors,
      errorSourceLabel,
    });
  }, [report, draft, chatErrors, errorSourceLabel]);

  const handleApplyProposal = useCallback((proposal: GuidelineChatProposal) => {
    setDraft((prev) => (prev ? applyProposalToDraftContent(prev, proposal) : prev));
  }, []);

  const handleCreateSession = useCallback(async () => {
    if (!reportId || !draft || creating) return;
    setCreating(true);
    try {
      await createTuningSessionApi({
        name: sessionName.trim() || `${report?.name ?? ''} — 調整`,
        baseline_report_id: reportId,
        content_json: draft,
      });
      toast.success('チューニングセッションが正常に作成されました', { title: 'セッション作成完了' });
      navigate(buildAccuracyReportDetailPath(reportId), { replace: true });
    } catch (e) {
      console.error('Tuning session creation failed:', e);
      toast.error('セッションの作成に失敗しました', { title: 'セッション作成失敗' });
    } finally {
      setCreating(false);
    }
  }, [reportId, draft, creating, sessionName, report?.name, navigate, toast]);

  if (!reportId) {
    return (
      <div className="accuracy-page">
        <div className="accuracy-card accuracy-empty">レポート ID が指定されていません。</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="accuracy-page accuracy-empty" style={{ padding: 48 }}>
        <Loader2 size={32} className="spin" />
        <p style={{ marginTop: 12 }}>ドラフトを読み込んでいます…</p>
      </div>
    );
  }

  const baselineBackLabel = '評価レポート';

  if (!draft || !chatContext) {
    return (
      <div className="accuracy-page">
        <Link to={buildAccuracyReportDetailPath(reportId)} className="accuracy-drilldown-back">
          <ArrowLeft size={16} />
          {baselineBackLabel}
        </Link>
        <div className="accuracy-card accuracy-empty">ドラフトを読み込めませんでした。</div>
      </div>
    );
  }

  return (
    <div className="accuracy-drilldown-page-shell">
      <div className="accuracy-drilldown-page-main">
        <Link to={buildAccuracyReportDetailPath(reportId)} className="accuracy-drilldown-back">
          <ArrowLeft size={16} />
          {baselineBackLabel}
        </Link>

        <header className="accuracy-drilldown-page-header">
          <h1>チューニングドラフト</h1>
        </header>

        <div className="accuracy-card accuracy-tuning-draft-editor-card">
          <div className="accuracy-tuning-draft-session-bar">
            <label className="accuracy-tuning-draft-session-name">
              <span className="accuracy-tuning-draft-session-name-label">セッション名</span>
              <input
                type="text"
                className="accuracy-tuning-draft-session-name-input"
                value={sessionName}
                onChange={(e) => setSessionName(e.target.value)}
                placeholder="セッション名を入力"
              />
            </label>
            <button
              type="button"
              className="accuracy-button primary accuracy-tuning-draft-session-create"
              disabled={creating}
              onClick={() => void handleCreateSession()}
            >
              {creating ? <Loader2 size={16} className="spin" /> : null}
              セッションを作成
            </button>
          </div>

          <GuidelineDraftEditor
            draft={draft}
            onChange={setDraft}
            compareTargets={compareTargets}
            compareTargetId={compareTargetId}
            onCompareTargetChange={setCompareTargetId}
          />
        </div>
      </div>

      <AccuracyGuidelineChatSidebar context={chatContext} onApplyProposal={handleApplyProposal} />
    </div>
  );
};

export default AccuracyTuningDraftPage;
